import React, { useState } from 'react';
import { Card, Steps, Space, Button, Tag, Timeline, Typography, Row, Col, Select, message, Collapse } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ExperimentOutlined, FormOutlined, CloudUploadOutlined, CheckCircleOutlined, InfoCircleOutlined, CaretRightOutlined } from '@ant-design/icons';
import { mockRegions } from '../mockData';

const { Text, Title } = Typography;

interface ReleaseInfo {
  id: string;
  creator: string;
  reviewer: string;
  createdAt: string;
  content: {
    paths: string[];
    OAMP: string[];
    beforeChange: string[];
    afterChange: string[];
  }[];
}

interface TimelineItem {
  time: string;
  title: string;
  description?: string;
}

export interface RegionDeployStatus {
  [key: string]: {
    status: 'success' | 'failure' | null;
    timestamp: string | null;
  };
}

interface GrayscaleReleaseProps {
  deployStatus: RegionDeployStatus;
  onDeployStatusChange: (status: RegionDeployStatus) => void;
}

const mockReleaseInfo: ReleaseInfo = {
  id: "release-001",
  creator: "buyue@agora.io",
  reviewer: "xiangwenquan@agora.io",
  createdAt: "2025-03-19 14:53:39",
  content: [
    {
      paths: ["/api/speech-to-text/v1/projects/(?-appid>\\w+)/join"],
      OAMP: ["/api/speech-to-text/v1/projects/(?-appid>\\w+)/join"],
      beforeChange: ["/api/speech-to-text/v1/projects/(?-appid>\\w+)/join"],
      afterChange: ["/rcv/api/speech-to-text/v1/projects/(?-appid>\\w+)/join"]
    }
  ]
};

const mockTimelineItems: TimelineItem[] = [
  {
    time: "2025-03-19 14:53",
    title: "dongguoxing@agora.io 创建版本"
  },
  {
    time: "2025-03-19 14:56",
    title: "dongguoxing@agora.io 发布到区域",
    description: "eu-frankfurt (#23), ap-tokyo (#23), na-oregon (#21), cn-shanghai (#20), eu-london (#19), ap-singapore (#18), na-virginia (#17), cn-beijing (#16)"
  },
  {
    time: "2025-03-19 15:00",
    title: "dongguoxing@agora.io 完成生产发布"
  }
];

const GrayscaleRelease: React.FC<GrayscaleReleaseProps> = ({ deployStatus, onDeployStatusChange }) => {
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [isDeploying, setIsDeploying] = useState(false);

  const handleRegionSelect = (value: string) => {
    setSelectedRegion(value);
  };

  const simulateDeployment = async () => {
    if (!selectedRegion) {
      message.warning('请先选择一个区域');
      return;
    }

    setIsDeploying(true);

    try {
      // 模拟发布过程
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate a random integer from 0 to 2 (inclusive) for exactly 1/3 probability of failure
      const randomInt = Math.floor(Math.random() * 3);
      const isSuccess = randomInt !== 2; // 0,1 = success (2/3), 2 = failure (1/3)
      
      const newDeployStatus: RegionDeployStatus = {
        ...deployStatus,
        [selectedRegion]: {
          status: isSuccess ? 'success' : 'failure',
          timestamp: new Date().toISOString()
        }
      };
      
      onDeployStatusChange(newDeployStatus);
      message.info(isSuccess ? '发布成功' : '发布失败');
    } finally {
      setIsDeploying(false);
      setSelectedRegion('');
    }
  };

  const renderDeployStatus = (regionId: string) => {
    const status = deployStatus[regionId];
    if (!status?.status) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: '8px'
      }}>
        <div style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: status.status === 'success' ? '#52c41a' : '#ff4d4f',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '8px'
        }}>
          {status.status === 'success' ? (
            <CheckCircleFilled style={{ color: 'white', fontSize: '16px' }} />
          ) : (
            <CloseCircleFilled style={{ color: 'white', fontSize: '16px' }} />
          )}
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column'
        }}>
          <span style={{ 
            color: status.status === 'success' ? '#52c41a' : '#ff4d4f', 
            fontWeight: 500, 
            fontSize: '14px' 
          }}>
            {status.status === 'success' ? '发布成功' : '发布失败'}
          </span>
          {status.timestamp && (
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
              {new Date(status.timestamp).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Row gutter={24}>
      <Col span={3} style={{ position: 'sticky', top: 0, height: '200vh' }}>
        <Card title="灰度发布流程" bordered={false} size="small" bodyStyle={{ padding: '24px 12px' }}>
          <Steps 
            direction="vertical" 
            current={-1}
            size="small"
            className="release-steps"
            items={[
              {
                title: '1.PreProd验证',
                description: '预生产配置变更',
                icon: <ExperimentOutlined />
              },
              {
                title: '2.创建版本',
                description: '创建后不可调整',
                icon: <FormOutlined />
              },
              {
                title: '3.生产发布',
                description: '选择区域发布',
                icon: <CloudUploadOutlined />
              },
              {
                title: '4.完成发布',
                description: '确认完成发布',
                icon: <CheckCircleOutlined />
              }
            ]}
          />
        </Card>
        <style>
          {`
            .release-steps .ant-steps-item {
              padding-bottom: 32px;
            }
            .release-steps .ant-steps-item-container {
              padding-bottom: 16px;
            }
          `}
        </style>
      </Col>

      <Col span={14}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Card title="发布到生产" bordered={false}>
            <Space direction="horizontal" style={{ width: '100%' }} align="start">
              <Select
                placeholder="选择区域"
                style={{ width: '500px' }}
                value={selectedRegion}
                onChange={handleRegionSelect}
                options={mockRegions.map(region => ({
                  label: (
                    <Space>
                      {region.name}
                      {region.tags?.map(tag => (
                        <Tag key={tag} color="orange">{tag}</Tag>
                      ))}
                      {deployStatus[region.id]?.status && (
                        <Tag color={deployStatus[region.id].status === 'success' ? 'success' : 'error'}>
                          {deployStatus[region.id].status === 'success' ? '发布成功' : '发布失败'}
                        </Tag>
                      )}
                    </Space>
                  ),
                  value: region.id
                }))}
              />
              <Button 
                type="primary" 
                onClick={simulateDeployment}
                loading={isDeploying}
                disabled={!selectedRegion || isDeploying}
              >
                发布
              </Button>
              <Button type="primary" danger>完成发布</Button>
            </Space>
          </Card>

          <Card title="版本号: v20250325-1" bordered={false}>
            {mockReleaseInfo.content.map((item, index) => (
              <div key={index}>
                <Title level={5}></Title>
                <div>
                  <Text>创建人：{mockReleaseInfo.reviewer}</Text>
                </div>
                <div>
                  <Text>创建时间：{mockReleaseInfo.createdAt}</Text>
                </div>
                <Text>路由 huzhou1 路由 stt-global.route.6ad44cd7 属性: paths</Text>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">OAMP:</Text>
                  <div style={{ background: '#f5f5f5', padding: 8, marginTop: 4 }}>
                    {item.OAMP.map((path, i) => (
                      <div key={i}>{path}</div>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">线上网关:</Text>
                  <div style={{ background: '#f5f5f5', padding: 8, marginTop: 4 }}>
                    {item.beforeChange.map((path, i) => (
                      <div key={i}>{path}</div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </Space>
      </Col>

      <Col span={6}>
        <Card title="发布 Timeline" bordered={false}>
          <Timeline
            items={mockTimelineItems.map(item => ({
              children: (
                <div>
                  <div>{item.time}</div>
                  <div>{item.title}</div>
                  {item.description && (
                    <div style={{ color: '#666', fontSize: '12px' }}>{item.description}</div>
                  )}
                </div>
              )
            }))}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default GrayscaleRelease; 